/**
 * Auth0 Provider wrapper for the application.
 *
 * This component wraps the Auth0 React SDK provider and configures it
 * with the appropriate settings for the GLTB application.
 */

import { Auth0Provider } from "@auth0/auth0-react";
import { useCallback, useMemo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { getAuth0Config } from "./config";

interface TAuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: TAuthProviderProps) {
    const navigate = useNavigate();
    const config = getAuth0Config();

    const onRedirectCallback = useCallback(
        (appState?: { returnTo?: string }) => {
            navigate(appState?.returnTo || "/", { replace: true });
        },
        [navigate],
    );

    const authorizationParams = useMemo(
        () => ({
            redirect_uri: config.redirectUri,
            audience: config.audience,
            scope: "openid profile email",
        }),
        [config.redirectUri, config.audience],
    );

    return (
        <Auth0Provider
            domain={config.domain}
            clientId={config.clientId}
            authorizationParams={authorizationParams}
            onRedirectCallback={onRedirectCallback}
            useRefreshTokens={true}
            cacheLocation="localstorage"
        >
            {children}
        </Auth0Provider>
    );
}
